import { createNewEndpoint } from '../core/endpoint';
import { AuthGuard } from '../core/guards/authGuard';
import { PreventUnauthenticatedGuard } from '../core/guards/preventUnauthenticated';
import { GraphQlMethods } from '../core/fetch';
import { FollowersActionType } from './actionTypes';
import { FOLLOW_USER_MUTATION } from './mutations/FollowUser';
import { UNFOLLOW_USER_MUTATION } from './mutations/UnfollowUser';
import { USERS_FOLLOWED_QUERY } from './queries/GetUsersFollowed';
import {
  FollowUserMutationResponse,
  FollowUserMutationVariables,
} from './types/FollowUser';
import {
  FollowedUserInfo,
  UsersFollowedCache,
  UsersFollowedQueryResponse,
  UsersFollowedQueryVariables,
} from './types/GetUsersFollowed';
import {
  UnfollowUserMutationResponse,
  UnfollowUserMutationVariables,
} from './types/UnfollowUser';
import {
  FollowedUser,
  FollowedUserConnection,
  FollowedUserData,
} from './types/common';
import {
  FollowedUsersQueryResponse,
  FollowedUsersQueryVariables,
  GivenUserFollowingCache,
} from './types/GetFollowedUsers';
import { FOLLOWED_USERS_QUERY } from './queries/GetFollowedUsers';
import {
  GivenUserFollowersCache,
  UsersFollowingQueryResponse,
  UsersFollowingQueryVariables,
} from './types/GetUsersFollowing';
import { USERS_FOLLOWING_QUERY } from './queries/GetUsersFollowing';
import { Interceptor } from '../core/interceptors';
import { ResponseResult } from '../core/request';
import { getPrivateProfile } from '../user/endpoints';

export const FOLLOW_QUERY_LIMIT = 25;

export const getUsersFollowedByCurrentUser = createNewEndpoint<
  UsersFollowedQueryResponse,
  UsersFollowedQueryVariables,
  FollowedUserInfo[],
  UsersFollowedCache
>({
  id: FollowersActionType.GetUsersFollowed,
  query: USERS_FOLLOWED_QUERY,
  method: GraphQlMethods.Get,
  guards: [PreventUnauthenticatedGuard],
  transform: (response) => response.me.usersFollowed,
  cacheTransform: ({ data }, current) => {
    if (!data) return {};

    const incoming = data?.reduce(
      (acc, follower) => ({
        ...acc,
        [follower.userId]: follower.isFollowed,
      }),
      {} as UsersFollowedCache
    );

    return { ...current, ...incoming };
  },
});

export const followUser = createNewEndpoint<
  FollowUserMutationResponse,
  FollowUserMutationVariables,
  FollowedUser,
  void
>({
  id: FollowersActionType.FollowUser,
  query: FOLLOW_USER_MUTATION,
  method: GraphQlMethods.Post,
  guards: [AuthGuard],
  transform: (response) => response.me.followUser,
  cacheTransform: ({ data, variables }) => {
    if (!data || !variables) return;

    const current = getUsersFollowedByCurrentUser.cache$.value || {};
    getUsersFollowedByCurrentUser.cache$.next({
      ...current,
      [variables.userId]: true,
    });

    const profileCache = getPrivateProfile.cache$.value || {};
    if (profileCache.profile) {
      getPrivateProfile.cache$.next({
        ...profileCache,
        profile: {
          ...profileCache.profile,
          totalUsersFollowing: +profileCache.profile.totalUsersFollowing + 1,
        },
      });
    }
  },
});

export const unfollowUser = createNewEndpoint<
  UnfollowUserMutationResponse,
  UnfollowUserMutationVariables,
  FollowedUserData,
  void
>({
  id: FollowersActionType.UnfollowUser,
  query: UNFOLLOW_USER_MUTATION,
  method: GraphQlMethods.Post,
  guards: [AuthGuard],
  transform: (response) => response.me.unfollowUser,
  cacheTransform: ({ data, variables }) => {
    if (!data || !variables) return;

    const current = getUsersFollowedByCurrentUser.cache$.value || {};
    getUsersFollowedByCurrentUser.cache$.next({
      ...current,
      [variables.userId]: false,
    });

    const profileCache = getPrivateProfile.cache$.value || {};
    if (profileCache.profile) {
      getPrivateProfile.cache$.next({
        ...profileCache,
        profile: {
          ...profileCache.profile,
          totalUsersFollowing: profileCache.profile.totalUsersFollowing - 1,
        },
      });
    }
  },
});

const getGivenUserFollowersInterceptor: Interceptor<
  ResponseResult<FollowedUserConnection, unknown>,
  unknown
> = ({ incoming }) => {
  if (!incoming.data || incoming.loading) return;
  const userIds = incoming.data.edges.map(({ user }) => user.id);
  getUsersFollowedByCurrentUser.requestAsPromise({ userIds });
};

export const getGivenUserFollowers = createNewEndpoint<
  UsersFollowingQueryResponse,
  UsersFollowingQueryVariables,
  FollowedUserConnection,
  GivenUserFollowersCache
>({
  id: FollowersActionType.GetUsersFollowing,
  query: USERS_FOLLOWING_QUERY,
  method: GraphQlMethods.Get,
  transform: (response) => response.public.usersFollowing,
  cacheTransform: ({ data, loading, variables }, current) => {
    if (!data || !variables) return current;

    if (variables.after) {
      const newEdges = data.edges;
      const currentEdges = current?.followers?.edges;
      return {
        followers: {
          edges: [...(currentEdges || []), ...newEdges],
          pageInfo: data.pageInfo,
        },
        loading,
      };
    }

    return { followers: data, loading };
  },
});

export const getUsersFollowedByGivenUser = createNewEndpoint<
  FollowedUsersQueryResponse,
  FollowedUsersQueryVariables,
  FollowedUserConnection,
  GivenUserFollowingCache
>({
  id: FollowersActionType.GetFollowedUsers,
  query: FOLLOWED_USERS_QUERY,
  method: GraphQlMethods.Get,
  interceptors: [getGivenUserFollowersInterceptor],
  transform: (response) => response.public.followedUsers,
  cacheTransform: ({ data, loading, variables }, current) => {
    if (!data || !variables) return current;

    if (variables.after) {
      const newEdges = data.edges;
      const currentEdges = current?.following?.edges;
      return {
        following: {
          edges: [...(currentEdges || []), ...newEdges],
          pageInfo: data.pageInfo,
        },
        loading,
      };
    }

    return { following: data, loading };
  },
});
